import { memo, useCallback, useState } from "react";

// Servisni sloj
import { FleetVehicleOfferSaleRow } from "services/api/fleet/vehicles/offers/types";

// UI
import { CardSelect } from "ui/components/CardSelect";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import FleetOfferSaleDropdown from "ui/components/Dropdown/FleetOfferSaleDropdown/FleetOfferSaleDropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { DatePicker } from "ui/components/DatePicker";
import { NumberField } from "ui/components/TextField/NumberField";

import { VEHICLE_RESERVATION_SOURCE_OPTIONS } from "../consts";
import { getStatusField } from "../fields";
import { VehicleReservationSourceEnum, VehicleStatusFieldsProps } from "../types";

/**
 * Rezervirano. Odgovara `VehicleStatusReservationDetail` na backendu — kupac, ponuda i kapara
 * su neobavezni, pa se prazan extender ne piše; obavezno je samo "rezervirano do",
 * koje kao vremenska crta stoji u baznom zapisu.
 *
 * Ponuda već nosi kupca i rok, pa ih odabir ponude popunjava i zaključava — dva izvora
 * iste vrijednosti bi se s vremenom razišla. Ispravak ide na ponudi ili prelaskom na
 * ručni unos; vrijednosti se svejedno spremaju uz status, da se zna što je vrijedilo
 * u trenutku rezervacije.
 */
const VehicleStatusReservationFields: React.FC<VehicleStatusFieldsProps> = (props) => {
  const { status, vehicleId, values, errors, onChange } = props;

  // Kod ponovnog otvaranja modal se montira iznova, pa je zatečena ponuda dovoljan trag
  // o tome što je bilo odabrano.
  const [source, setSource] = useState<VehicleReservationSourceEnum>(
    values.offerSaleId
      ? VehicleReservationSourceEnum.OFFER
      : VehicleReservationSourceEnum.MANUAL
  );

  const contactField = getStatusField(status, "contact");
  const offerSaleField = getStatusField(status, "offerSale");
  const validUntilField = getStatusField(status, "validUntil");
  const depositAmountField = getStatusField(status, "depositAmount");

  const isOffer = source === VehicleReservationSourceEnum.OFFER;

  const handleSourceChange = useCallback(
    (id: string) => {
      setSource(id as VehicleReservationSourceEnum);

      // Promjena izvora briše ono što je došlo od prethodnog — inače bi uz ručni unos
      // ostala zalijepljena ponuda i njezini podaci.
      onChange({ offerSaleId: null, contactId: null, validUntil: null });
    },
    [onChange]
  );

  const handleDropdownChange = useCallback(
    (_e: any, name: string, item: DropdownOption) => {
      onChange({ [name]: item.value || null });
    },
    [onChange]
  );

  // Odabir ponude popunjava kupca i rok; polja su tada zaključana, pa se mijenjaju
  // samo promjenom ponude.
  const handleOfferSelect = useCallback(
    (selected: FleetVehicleOfferSaleRow | null) => {
      onChange({
        contactId: selected?.contactClient?.id ?? null,
        validUntil: selected?.dueDate ?? null,
      });
    },
    [onChange]
  );

  const handleDateChange = useCallback(
    (name: string, value: Date | null) => {
      onChange({ [name]: value });
    },
    [onChange]
  );

  const handleNumberChange = useCallback(
    (value: number | null, name?: string) => {
      if (!name) return;
      onChange({ [name]: value });
    },
    [onChange]
  );

  return (
    <div className="grid grid-cols-2 gap-3">
      {offerSaleField && (
        <div className="col-span-2">
          <CardSelect
            options={VEHICLE_RESERVATION_SOURCE_OPTIONS}
            value={source}
            onChange={handleSourceChange}
            columns={2}
          />
        </div>
      )}

      {/* Ponuda i rok koji iz nje dolazi stoje jedno uz drugo — čitaju se zajedno. */}
      {isOffer && offerSaleField && (
        <div>
          <FleetOfferSaleDropdown
            name="offerSaleId"
            label={offerSaleField.label}
            placeholder="Odaberite ponudu"
            vehicleId={vehicleId}
            value={values.offerSaleId}
            onChange={handleDropdownChange}
            onOfferSelect={handleOfferSelect}
            error={errors.offerSaleId}
            required
            width="full"
          />
        </div>
      )}

      {/* Uz ponudu se kupac ne prikazuje zasebno — stoji uz broj ponude u dropdownu.
          Svejedno se sprema uz status, preuzet iz odabrane ponude. */}
      {contactField && !isOffer && (
        <div>
          <ContactDropdown
            name="contactId"
            label={contactField.label}
            placeholder={`Odaberite: ${contactField.label.toLowerCase()}`}
            value={values.contactId}
            onChange={handleDropdownChange}
            contactGroupIds={contactField.contactGroupIds}
            error={errors.contactId}
            required={contactField.required}
            width="full"
          />
        </div>
      )}

      {validUntilField && (
        <div>
          <DatePicker
            name="validUntil"
            label={validUntilField.label}
            placeholder={validUntilField.label}
            value={values.validUntil}
            onChange={handleDateChange}
            error={errors.validUntil}
            required={validUntilField.required}
            disabled={isOffer}
            width="full"
          />
        </div>
      )}

      {/* Kapara se upisuje u oba slučaja — ponuda je ne nosi. */}
      {depositAmountField && (
        <div>
          <NumberField
            name="depositAmount"
            label={depositAmountField.label}
            placeholder={depositAmountField.label}
            value={values.depositAmount}
            onChange={handleNumberChange}
            error={errors.depositAmount}
            groupAppend="€"
            size="full"
          />
        </div>
      )}
    </div>
  );
};

export default memo(VehicleStatusReservationFields);
